import { Box, garmentPatternShapes, ShapeNode } from 'shared/kit/garment-shapes';
import { KitElement } from 'shared/kit/kit-element';
import { DEFAULT_COLOR, KitOutfit, makeDefaultElementState } from 'shared/kit/kit-outfit';
import {
  KIT_SHIRT_PATH,
  KIT_SHIRT_PATTERN_BOX,
  KIT_SHIRT_TRANSFORM,
  KIT_SHORTS_PATH,
  KIT_SHORTS_PATTERN_BOX,
  KIT_SOCKS_PATH,
  KIT_SOCKS_PATTERN_BOX,
  KIT_STROKE,
} from 'shared/kit/kit-paths';
import { KitPattern } from 'shared/kit/kit-pattern';

/**
 * Та же геометрия, что у `KitPreview` (`shared/components/kit/kit-preview.tsx`), но строкой:
 * канва получает форму через `fabric.Image.fromURL(kitSvgDataUrl(...))`.
 *
 * Отличия от превью — намеренные:
 * - нет фона-подложки: на табло форма лежит поверх графики шаблона;
 * - id клипов и градиентов получают уникальный суффикс на вызов (в превью они константы —
 *   BASELINE фазы 1 плана, два превью на странице склеиваются по клипу);
 * - `viewBox` обрезается по нарисованным элементам, иначе «только футболка» рисуется в верхней
 *   трети картинки, а две трети остаются пустыми.
 */

/** Рисуются только эти три элемента: кепки и шлема в Kit_blank.svg нет. */
export type DrawableKitElement = KitElement.SHIRT | KitElement.SHORTS | KitElement.SOCKS;

/** Порядок отрисовки как в Kit_blank.svg: гетры → шорты → футболка. */
const ELEMENT_ORDER: DrawableKitElement[] = [KitElement.SOCKS, KitElement.SHORTS, KitElement.SHIRT];

interface GarmentGeometry {
  path: string;
  /** Бокс узора; он же — габариты фигуры (см. `kit-paths.ts`). */
  box: Box;
  transform?: string;
  /** Сдвиг бокса, если у фигуры есть `transform`. */
  offset?: { x: number; y: number };
}

const GEOMETRY: Record<DrawableKitElement, GarmentGeometry> = {
  [KitElement.SOCKS]: { path: KIT_SOCKS_PATH, box: KIT_SOCKS_PATTERN_BOX },
  [KitElement.SHORTS]: { path: KIT_SHORTS_PATH, box: KIT_SHORTS_PATTERN_BOX },
  [KitElement.SHIRT]: {
    path: KIT_SHIRT_PATH,
    box: KIT_SHIRT_PATTERN_BOX,
    transform: KIT_SHIRT_TRANSFORM,
    offset: { x: 31, y: -5 },
  },
};

/** Запас на обводку (`KIT_STROKE.strokeWidth` = 1) по краям обрезанного viewBox. */
const VIEWBOX_PADDING = 1;

let callCounter = 0;

/** Уникальный на документ суффикс id: два комплекта на одной доске не должны делить клип. */
const nextIdSuffix = (): string => {
  callCounter += 1;
  return `k${callCounter}`;
};

const escapeAttr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** React-нотация примитивов → SVG-атрибуты. `viewBox` пишется руками: там регистр значим. */
const attrName = (name: string): string => name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const serializeAttrs = (attrs: Record<string, string | number>): string =>
  Object.entries(attrs)
    .map(([name, value]) => ` ${attrName(name)}="${escapeAttr(String(value))}"`)
    .join('');

const serializeShapes = (nodes: ShapeNode[]): string =>
  nodes
    .map((node) =>
      node.children?.length
        ? `<${node.tag}${serializeAttrs(node.attrs)}>${serializeShapes(node.children)}</${node.tag}>`
        : `<${node.tag}${serializeAttrs(node.attrs)}/>`,
    )
    .join('');

const strokeAttrs = serializeAttrs(KIT_STROKE);

const garmentMarkup = (element: DrawableKitElement, state: { pattern: KitPattern; colors: string[] }, idSuffix: string): string => {
  const geometry = GEOMETRY[element];
  const color = state.colors[0] || DEFAULT_COLOR;
  const clipId = `clip-${element}-${idSuffix}`;
  const open = geometry.transform ? `<g transform="${geometry.transform}">` : '<g>';

  const base =
    `<defs><clipPath id="${clipId}"><path d="${geometry.path}"/></clipPath></defs>` +
    `<path d="${geometry.path}" fill="${escapeAttr(color)}"${strokeAttrs}/>`;

  const hasPattern = state.pattern !== KitPattern.SOLID && Boolean(state.colors[1]);
  const pattern = hasPattern
    ? `<g clip-path="url(#${clipId})">` +
      serializeShapes(garmentPatternShapes(state.pattern, geometry.box, state.colors[1], `pat-${element}-${idSuffix}`)) +
      '</g>'
    : '';

  return `${open}${base}${pattern}</g>`;
};

const viewBoxFor = (elements: DrawableKitElement[]): { viewBox: string; width: number; height: number } => {
  const boxes = elements.map((element) => {
    const { box, offset } = GEOMETRY[element];
    return { ...box, x: box.x + (offset?.x ?? 0), y: box.y + (offset?.y ?? 0) };
  });

  const minX = Math.min(...boxes.map((b) => b.x)) - VIEWBOX_PADDING;
  const minY = Math.min(...boxes.map((b) => b.y)) - VIEWBOX_PADDING;
  const maxX = Math.max(...boxes.map((b) => b.x + b.w)) + VIEWBOX_PADDING;
  const maxY = Math.max(...boxes.map((b) => b.y + b.h)) + VIEWBOX_PADDING;

  const width = maxX - minX;
  const height = maxY - minY;
  return { viewBox: `${minX} ${minY} ${width} ${height}`, width, height };
};

/**
 * Что рисует плейсхолдер «Форма команды»: весь комплект или одну футболку. Хранится в
 * `object.data.kitElements`; старые доски поля не имеют — им `full`.
 */
export type KitElementsSetting = 'full' | 'shirt';

export const KIT_ELEMENTS_SETTINGS: KitElementsSetting[] = ['full', 'shirt'];

export const KIT_ELEMENTS_LABELS: Record<KitElementsSetting, string> = {
  full: 'Вся форма',
  shirt: 'Только футболка',
};

/** Настройка → список элементов для `buildKitSvgMarkup`. Нераспознанное значение = весь комплект. */
export const kitElementsOf = (setting?: KitElementsSetting): DrawableKitElement[] =>
  setting === 'shirt' ? [KitElement.SHIRT] : ELEMENT_ORDER;

export interface BuildKitSvgParams {
  /** Состояния элементов; отсутствующий элемент рисуется белым однотонным. */
  outfit: Partial<KitOutfit>;
  /** Что рисовать. По умолчанию — футболка, шорты и гетры. */
  elements?: KitElement[];
  /** Суффикс id — для тестов; в бою генерируется сам. */
  idSuffix?: string;
}

/** Разметка SVG формы команды. Всегда с `viewBox` и с `width`/`height` — без них fabric даёт нулевой размер. */
export function buildKitSvgMarkup({ outfit, elements, idSuffix }: BuildKitSvgParams): string {
  const requested = elements?.length ? elements : ELEMENT_ORDER;
  const drawn = ELEMENT_ORDER.filter((element) => requested.includes(element));

  if (drawn.length === 0) return '';

  const suffix = idSuffix ?? nextIdSuffix();
  const { viewBox, width, height } = viewBoxFor(drawn);
  const body = drawn.map((element) => garmentMarkup(element, outfit[element] ?? makeDefaultElementState(), suffix)).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${width}" height="${height}">` + body + '</svg>';
}

/** `data:image/svg+xml;base64,…` для `fabric.Image.fromURL`. */
export function kitSvgDataUrl(params: BuildKitSvgParams): string {
  const markup = buildKitSvgMarkup(params);
  // `btoa` не принимает символы вне latin1; разметка ASCII, но подстраховываемся на случай подписей.
  const latin1 = encodeURIComponent(markup).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  const base64 = window.btoa(latin1);
  return `data:image/svg+xml;base64,${base64}`;
}
